import { useMemo } from 'react';
import { GetServerSidePropsContext } from 'next';
import cookie from 'cookie';
import { Trans, useTranslation } from 'react-i18next';

import { Box, Flex, Link, Text, useToast } from '@chakra-ui/react';
import { CustomCertificate, ScaleContainer, ShareButton } from '@udacity/udacity-certificate';

import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import {
  LP_CERTIFICATE_PRINT_ROOT_ID,
  LpPrintRootDownloadButton,
} from '~/features/certificates/components/lp-print-root-download-button';
import { MarketingMessage } from '~/features/certificates/components/marketing-message';
import { getCertV2ByLearningPlanSubscriptionId } from '~/features/certificates/controllers/get-certificate';
import { useLocalizedCertificateV2 } from '~/features/certificates/hooks/use-localized-certificate-v2';
import { CertificateV2 } from '~/features/certificates/model';
import { setCertificatePublicCacheIfUnauthenticated } from '~/features/certificates/set-certificate-cache';
import { ensureCertificateUrlHasScheme } from '~/features/certificates/utils';
import { getServerSideLanguage } from '~/features/internationalization/utils/get-server-side-language';
import { getUserLearningPlans } from '~/features/learning-plans/controllers/get-user-learning-plans';
import { UserLearningPlans } from '~/features/learning-plans/models/user-learning-plans';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { Page } from '~/features/page/models/page';
import { getInternalPage } from '~/features/page/utils/get-internal-page';
import { getUserPreferences } from '~/features/user/controllers/get-user-preferences';
import { getUserIdFromJwt } from '~/features/user/utils/get-user-id-from-jwt';

type LpCertificate = NonNullable<CertificateV2>;

export default function PlanCertificatePage({
  page,
  initialCertificate,
  initialLanguage,
  subscriptionId,
  userLearningPlan,
}: {
  page: Page;
  initialCertificate: NonNullable<CertificateV2>;
  initialLanguage: string;
  userLearningPlan: UserLearningPlans[number] | null;
  subscriptionId: string;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { certificate: localizedCertificate } = useLocalizedCertificateV2({
    subscriptionId,
    initialLanguage,
  });
  const certificate: LpCertificate = localizedCertificate || initialCertificate;
  /** Host-only confirmation URLs resolve incorrectly on /certificate/lp/*; normalize for CustomCertificate + print clone. */
  const certificateForRender = useMemo<LpCertificate>(() => {
    const link = certificate.confirmationLink;
    if (!link?.url) return certificate;
    const absoluteUrl = ensureCertificateUrlHasScheme(link.url);
    if (absoluteUrl === link.url) return certificate;
    return {
      ...certificate,
      confirmationLink: { ...link, url: absoluteUrl },
    };
  }, [certificate]);

  /** Hide inline link from CustomCertificate; render labeled confirmation inside the certificate frame instead. */
  const certificateForCertificateComponent = useMemo<LpCertificate>(() => {
    if (!certificateForRender.confirmationLink?.url) return certificateForRender;
    return { ...certificateForRender, confirmationLink: null };
  }, [certificateForRender]);

  const confirmationHref = certificateForRender.confirmationLink?.url ?? '';

  const trackingAttributes = getTrackingAttributes(userLearningPlan);

  return (
    <Box>
      <style>
        {`
          @media print{
            html, body, #__next {
              overflow: hidden; !important
            }
          }
        `}
      </style>
      <PageHead page={page} pageCategory={PageCategory.Uncategorized} />
      <PageLayout isSimpleMode>
        <ContentContainer>
          <Flex
            gap='24px'
            id='certificate-container'
            maxW='100vw'
            flexDir={{
              base: 'column',
              md: 'row',
            }}
          >
            <Box
              w={{
                base: '100%',
                md: '67%',
              }}
            >
              <ScaleContainer>
                <Box h='8.5in' id={LP_CERTIFICATE_PRINT_ROOT_ID} maxW='100%' position='relative' w='11in'>
                  <CustomCertificate certificate={certificateForCertificateComponent} />
                  {confirmationHref ? (
                    <Box
                      bottom='36px'
                      id='lp-certificate-confirmation'
                      left='70px'
                      maxW='calc(100% - 140px)'
                      position='absolute'
                      right='70px'
                      zIndex={1}
                    >
                      <Text color='black' display='block' fontSize='xs' lineHeight='short' mb={1}>
                        {t('certificate.confirmationParticipation')}
                      </Text>
                      <Text color='black' display='block' fontSize='xs' lineHeight='short'>
                        <Trans
                          i18nKey='certificate.confirmationAt'
                          values={{ url: confirmationHref }}
                          components={{
                            1: (
                              <Link
                                color='blue.700'
                                fontSize='xs'
                                href={confirmationHref}
                                textDecoration='underline'
                                isExternal
                              />
                            ),
                          }}
                        />
                      </Text>
                    </Box>
                  ) : null}
                </Box>
              </ScaleContainer>
            </Box>
            <Box flex={1} minW='300px'>
              <MarketingMessage />
              <Flex gap='16px'>
                <LpPrintRootDownloadButton />
                {userLearningPlan && (
                  <ShareButton
                    confirmUrl={getConfirmUrl(subscriptionId)}
                    graduationDate={certificate.completionDate.content}
                    programTitle={userLearningPlan.title}
                    onCopyToClipboardClick={() => {
                      toast({
                        variant: 'success',
                        title: t('certificate.linkCopied'),
                      });
                    }}
                    onLinkedInClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Share Learning Plan Certificate on LinkedIn Clicked',
                        label: 'Share on LinkedIn',
                        action: 'share_lp_certificate_on_linkedin_clicked',
                        additionalAttributes: trackingAttributes,
                      });
                    }}
                    onShareClick={() => {
                      trackEvent({
                        type: 'click',
                        name: 'Share Learning Plan Certificate Clicked',
                        label: 'Share',
                        action: 'share_lp_certificate_clicked',
                        additionalAttributes: trackingAttributes,
                      });
                    }}
                  />
                )}
              </Flex>
            </Box>
          </Flex>
        </ContentContainer>
      </PageLayout>
    </Box>
  );
}

export const getServerSideProps = async ({ req, res, params }: GetServerSidePropsContext) => {
  const cookies = cookie.parse(req.headers.cookie ?? '');
  const jwt = cookies._jwt;

  const subscriptionId = params?.subscriptionId;
  if (!subscriptionId) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  const userPreferences = jwt ? await getUserPreferences({ jwt }) : undefined;
  const initialLanguage = getServerSideLanguage(req, userPreferences);

  let certificate: CertificateV2 | null = null;

  try {
    certificate = await getCertV2ByLearningPlanSubscriptionId({
      subscriptionId: subscriptionId as string,
      preferredLanguage: initialLanguage,
    });
  } catch {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  if (!certificate) {
    setCertificatePublicCacheIfUnauthenticated(res, jwt);
    return {
      notFound: true,
    };
  }

  const page = getInternalPage({ eventName: 'Certificate Page Viewed', nofollow: false });
  const userId = jwt ? getUserIdFromJwt(jwt) : undefined;

  const userLearningPlans = userId ? await getUserLearningPlans({ userId, jwt, forcedLanguage: initialLanguage }) : [];
  const userLearningPlan = userLearningPlans.find((plan) => plan.subscriptionId === subscriptionId);

  setCertificatePublicCacheIfUnauthenticated(res, jwt);

  return {
    props: {
      page,
      initialCertificate: certificate,
      initialLanguage,
      subscriptionId,
      userLearningPlan: userLearningPlan ?? null,
    },
  };
};

function getConfirmUrl(subscriptionId: string) {
  if (typeof window === 'undefined') {
    return `https://www.udacity.com/certificate/lp/${subscriptionId}`;
  }
  return window.location.href;
}

function getTrackingAttributes(userLearningPlan: UserLearningPlans[number] | null) {
  if (!userLearningPlan) return {};

  const purchaseId = userLearningPlan.tags?.find((tag) => tag.tagType === 'PURCHASE_ID')?.tagKey;
  const contractId = userLearningPlan.tags?.find((tag) => tag.tagType === 'ENTERPRISE_CONTRACT')?.tagKey;

  return {
    plan_id: userLearningPlan.id,
    company_id: userLearningPlan.companyId,
    purchase_id: purchaseId,
    contract_id: contractId,
  };
}
